import React from "react";
import {
  ArrowRight,
  ClipboardCheck,
  Activity,
  ShieldAlert,
  HeartHandshake,
  Sparkles,
  Stethoscope,
  Wallet,
  Crown,
  MonitorPlay,
  Building2,
  Layers,
} from "lucide-react";
import { BRAND, MEDIA } from "../lib/constants";
import CTABand from "../components/CTABand";
import ShieldMotif from "../components/ShieldMotif";
import { useSEO } from "../lib/seo";

const TOPICS = [
  {
    icon: ClipboardCheck,
    title: "MDS Documentation and Accuracy",
    body: "Section by section coding precision, supporting documentation discipline, and the daily habits that hold MDS integrity together.",
  },
  {
    icon: Activity,
    title: "QAPI and Quality Measures",
    body: "Translating CMS quality measure data into operational priorities the IDT can actually move, supported by a QAPI committee that drives change.",
  },
  {
    icon: ShieldAlert,
    title: "Survey Preparedness for Staff",
    body: "How surveyors think, how to handle entrance and observation, and how to respond to interviews without weakening the facility's position.",
  },
  {
    icon: HeartHandshake,
    title: "Resident Rights and Abuse Prevention",
    body: "Federal and state resident rights frameworks, mandatory reporting, and the cultural standards that prevent abuse before it begins.",
  },
  {
    icon: Sparkles,
    title: "Infection Control Compliance",
    body: "IPC fundamentals, surveillance, outbreak readiness, and documentation that holds up under both survey and litigation scrutiny.",
  },
  {
    icon: Stethoscope,
    title: "Care Planning Best Practices",
    body: "Individualized, defensible care plans that drive care. Not paperwork written to satisfy regulation.",
  },
  {
    icon: Wallet,
    title: "Medicaid Billing Basics for Clinical Staff",
    body: "How clinical documentation drives the Medicaid dollar, and what bedside teams must know to protect facility revenue.",
  },
  {
    icon: Crown,
    title: "Leadership and Charge Nurse Development",
    body: "Building floor level leaders through accountability conversations, IDT facilitation, conflict resolution, and operational ownership.",
  },
];

const FORMATS = [
  {
    icon: Building2,
    title: "On Site Inservices",
    body: "Single session trainings delivered inside the facility, calibrated to a specific department or topic. Typically 60 to 120 minutes with workbooks and follow up resources.",
    tag: "In Person",
  },
  {
    icon: MonitorPlay,
    title: "Virtual and Zoom Sessions",
    body: "Live, interactive sessions for staff across multiple facilities or for teams that prefer a remote training rhythm. Recordings available for ongoing reference.",
    tag: "Remote",
  },
  {
    icon: Layers,
    title: "Custom Multi Session Packages",
    body: "Multi week training curricula tailored to an operator's portfolio. Combines on site, virtual, and asynchronous components. Ideal for ownership groups standardizing performance across a portfolio.",
    tag: "Tailored",
  },
];

export default function Inservices() {
  useSEO({
    title:
      "Inservices & Staff Training · Skilled Nursing & LTC · Missouri",
    description:
      "On-site and virtual staff training for Missouri skilled nursing facilities and long-term care organizations: MDS accuracy, QAPI, survey preparedness, resident rights, infection control, care planning, Medicaid billing, and leadership development.",
    path: "/inservices-training",
  });
  return (
    <div data-testid="inservices-page" className="bg-ivory">
      {/* HERO */}
      <section className="section-pad pt-32 relative overflow-hidden bg-paper">
        <ShieldMotif
          className="absolute -right-40 top-1/4 w-[560px] hidden lg:block"
          opacity={0.06}
        />
        <div className="container-luxe relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="overline mb-5">Inservices and Training</div>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-navy leading-[0.95] mb-6">
                Build your team.<br />
                <span className="italic text-teal">Strengthen your facility.</span>
              </h1>
              <p className="font-sans text-lg sm:text-xl text-navy/75 max-w-2xl leading-relaxed mb-10">
                On site and virtual staff training for skilled nursing and
                long-term care facilities, designed by Sierra Thomas. Each
                session translates regulatory literacy, clinical documentation,
                and operational discipline into measurable performance at the
                bedside.
              </p>
              <a
                href={BRAND.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary group"
                data-testid="inservices-hero-cta"
              >
                Book a Training Conversation
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
            <div className="lg:col-span-4">
              <div className="grid grid-cols-2 gap-px bg-champagne-200 border border-champagne-200">
                {[
                  { k: "8+", v: "Signature Topics" },
                  { k: "On Site", v: "Or Virtual" },
                  { k: "60-120", v: "Min Sessions" },
                  { k: "Custom", v: "Curricula" },
                ].map((s) => (
                  <div key={s.v} className="bg-ivory p-5">
                    <div className="font-serif text-2xl text-navy">{s.k}</div>
                    <div className="text-[10px] uppercase tracking-overline text-gold mt-1">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="section-pad" data-testid="inservices-philosophy">
        <div className="container-luxe grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline mb-5">Training Philosophy</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-navy leading-tight">
              Training that{" "}
              <span className="italic text-teal">changes the floor.</span> Not
              the slide deck.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6 font-sans text-navy/80 leading-relaxed text-base sm:text-lg">
            <p>
              Most inservices end the moment staff sign the attendance sheet.
              AthenaCrest trainings are built differently. Every session ties
              a regulatory or clinical concept to a daily operational behavior
              the team can install Monday morning.
            </p>
            <p>
              We bring the same operational fluency we use inside facility
              stabilization engagements into every classroom. The result is
              training that survey teams notice, that administrators can
              measure, and that staff actually keep using.
            </p>
          </div>
        </div>
      </section>

      {/* TOPICS */}
      <section
        className="section-pad bg-paper"
        data-testid="inservices-topics"
      >
        <div className="container-luxe">
          <div className="flex flex-col lg:flex-row justify-between items-start gap-8 mb-16">
            <div className="max-w-2xl">
              <div className="overline mb-5">Signature Topics</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy leading-[1.05]">
                Eight curricula built from inside the building.
              </h2>
            </div>
            <p className="font-sans text-base text-navy/65 max-w-md">
              Every topic is calibrated to the audience, whether bedside
              clinical staff, department heads, the IDT, or executive
              leadership. Custom topics available on request.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-champagne-200 border border-champagne-200">
            {TOPICS.map((t, i) => (
              <div
                key={t.title}
                className="bg-ivory p-8 hover:bg-champagne-100 transition-colors duration-300 group"
                data-testid={`topic-card-${i}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 border border-gold flex items-center justify-center group-hover:bg-gold transition-colors">
                    <t.icon className="h-5 w-5 text-gold group-hover:text-navy" />
                  </div>
                  <span className="font-serif text-gold text-xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-serif text-xl text-navy mb-3 group-hover:text-teal transition-colors leading-snug">
                  {t.title}
                </h3>
                <p className="font-sans text-sm text-navy/70 leading-relaxed">
                  {t.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="section-pad" data-testid="inservices-formats">
        <div className="container-luxe">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-5">
              <div className="overline mb-5">Format Options</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy leading-[1.05]">
                Calibrated to your{" "}
                <span className="italic text-teal">team and timeline.</span>
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="font-sans text-lg text-navy/70 leading-relaxed">
                AthenaCrest delivers training in the format that fits the
                facility's operational rhythm. That may be a single on site
                inservice during shift change, a virtual session across a multi
                state portfolio, or a multi week leadership curriculum that
                compounds across the organization.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {FORMATS.map((f) => (
              <div
                key={f.title}
                className="luxe-card group flex flex-col"
                data-testid={`format-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 border border-gold flex items-center justify-center group-hover:bg-gold transition-colors">
                    <f.icon className="h-5 w-5 text-gold group-hover:text-navy" />
                  </div>
                  <span className="text-[10px] tracking-overline uppercase text-gold border border-gold/40 px-3 py-1">
                    {f.tag}
                  </span>
                </div>
                <h3 className="font-serif text-2xl text-navy mb-4 group-hover:text-teal transition-colors leading-tight">
                  {f.title}
                </h3>
                <p className="font-sans text-sm text-navy/70 leading-relaxed flex-1">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE BREAK */}
      <section
        className="relative h-[420px] overflow-hidden"
        data-testid="inservices-image-break"
      >
        <img
          src={MEDIA.hospitalHallway}
          alt="Inside a long-term care facility"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/70 to-navy/30" />
        <div className="absolute inset-0 flex items-center">
          <div className="container-luxe relative z-10 max-w-3xl">
            <div className="overline mb-5" style={{ color: "#C5A059" }}>
              Inside the Building
            </div>
            <p className="font-serif text-3xl sm:text-4xl text-ivory italic leading-tight">
              "Training is the cheapest insurance a facility ever buys. It is
              also the most expensive thing to skip."
            </p>
            <p className="mt-6 font-sans text-sm tracking-overline uppercase text-ivory/65">
              Sierra Thomas
            </p>
          </div>
        </div>
      </section>

      <CTABand
        testId="inservices-cta-band"
        eyebrow="Schedule Training"
        headline="Bring AthenaCrest training to your team."
        sub="A 30 minute confidential conversation maps the right topics, the right audience, and the right format for your facility or your portfolio."
      />
    </div>
  );
}
